import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Lightbulb, CheckCircle2, ArrowRight, ArrowLeft,
  Target, Users, Cog, TrendingUp, Shield, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const stages = [
  {
    stage: '1',
    title: 'Assessment & Discovery',
    icon: Target,
    description: 'Understand where you are before deciding where to go.',
    questions: [
      'What manual processes consume the most staff time?',
      'Where do errors and delays most frequently occur?',
      'What information is trapped in spreadsheets or paper files?',
      'Which customer touchpoints feel outdated or friction-filled?',
      'What reports take hours to compile that should take minutes?',
      'Where do communication breakdowns happen most often?',
    ],
    actions: [
      'Document current workflows in detail',
      'Interview staff about daily pain points',
      'Audit existing technology and its usage',
      'Identify quick wins vs. long-term projects',
      'Calculate the cost of inefficiency (staff hours × hourly rate)',
    ]
  },
  {
    stage: '2',
    title: 'Strategy & Prioritization',
    icon: Lightbulb,
    description: 'You can\'t transform everything at once. Prioritize ruthlessly.',
    questions: [
      'Which improvements would have the biggest impact on revenue?',
      'What changes would make the biggest difference for customers?',
      'Which projects have the best effort-to-impact ratio?',
      'What dependencies exist between different initiatives?',
      'What\'s realistic given your budget and team capacity?',
    ],
    framework: {
      title: 'Prioritization Matrix',
      items: [
        { priority: 'Do First', criteria: 'High impact, low effort', example: 'Switch to online scheduling' },
        { priority: 'Plan For', criteria: 'High impact, high effort', example: 'Implement new CRM system' },
        { priority: 'Quick Wins', criteria: 'Low impact, low effort', example: 'Automate email responses' },
        { priority: 'Avoid', criteria: 'Low impact, high effort', example: 'Custom-build commodity tools' },
      ]
    }
  },
  {
    stage: '3',
    title: 'Foundation Building',
    icon: Cog,
    description: 'Get the basics right before adding complexity.',
    essentials: [
      { area: 'Cloud Infrastructure', what: 'Move from local servers to cloud services', why: 'Enables remote work, automatic backups, and scalability' },
      { area: 'Central Data System', what: 'Single source of truth for customer/business data', why: 'Eliminates data silos and duplicate entry' },
      { area: 'Communication Platform', what: 'Unified system for team communication', why: 'Reduces email overload and lost messages' },
      { area: 'Security Basics', what: 'Password management, 2FA, backup systems', why: 'Foundation for safe digital operations' },
      { area: 'Standard Operating Procedures', what: 'Document how things should be done', why: 'Enables training, consistency, and improvement' },
    ]
  },
  {
    stage: '4',
    title: 'Process Automation',
    icon: Zap,
    description: 'Automate repetitive tasks to free up human creativity.',
    opportunities: [
      { process: 'Customer Onboarding', manual: 'Sending welcome emails, scheduling calls, sharing documents', automated: 'Automated email sequences, self-scheduling, document portals' },
      { process: 'Invoicing & Payments', manual: 'Creating invoices, sending reminders, recording payments', automated: 'Automatic invoice generation, payment reminders, reconciliation' },
      { process: 'Appointment Scheduling', manual: 'Back-and-forth emails to find times', automated: 'Self-service booking with calendar integration' },
      { process: 'Lead Follow-up', manual: 'Remembering to follow up, sending individual emails', automated: 'CRM reminders, automated nurture sequences' },
      { process: 'Reporting', manual: 'Pulling data from multiple sources, creating spreadsheets', automated: 'Real-time dashboards, scheduled report delivery' },
    ]
  },
  {
    stage: '5',
    title: 'Customer Experience Enhancement',
    icon: Users,
    description: 'Use technology to serve customers better, not replace human connection.',
    improvements: [
      { touchpoint: 'First Contact', enhancement: 'Live chat, instant quote tools, AI-powered FAQ' },
      { touchpoint: 'Purchasing', enhancement: 'Online ordering, digital contracts, easy payment options' },
      { touchpoint: 'Service Delivery', enhancement: 'Real-time tracking, customer portals, proactive updates' },
      { touchpoint: 'Support', enhancement: 'Multi-channel support, knowledge base, chatbots for basics' },
      { touchpoint: 'Feedback', enhancement: 'Automated surveys, review requests, sentiment analysis' },
    ]
  },
  {
    stage: '6',
    title: 'Optimization & Growth',
    icon: TrendingUp,
    description: 'Measure, learn, and continuously improve.',
    metrics: [
      { metric: 'Process Efficiency', measure: 'Time to complete key tasks before vs. after' },
      { metric: 'Error Rate', measure: 'Mistakes requiring rework or correction' },
      { metric: 'Customer Satisfaction', measure: 'NPS scores, review ratings, repeat business' },
      { metric: 'Staff Productivity', measure: 'Output per person, overtime hours' },
      { metric: 'Revenue per Employee', measure: 'Total revenue divided by headcount' },
      { metric: 'Technology ROI', measure: 'Value delivered vs. cost invested' },
    ],
    practices: [
      'Review metrics monthly',
      'Gather team feedback on new tools quarterly',
      'Survey customers about digital experience',
      'Stay informed about new technology options',
      'Plan annual technology roadmap reviews',
    ]
  },
];

const commonMistakes = [
  { mistake: 'Automating bad processes', fix: 'Fix the process first, then automate it' },
  { mistake: 'Too many tools at once', fix: 'Master one system before adding another' },
  { mistake: 'Ignoring team input', fix: 'Involve users in selection and implementation' },
  { mistake: 'Skipping training', fix: 'Budget time and money for proper onboarding' },
  { mistake: 'No clear ownership', fix: 'Assign someone to own each system' },
  { mistake: 'Choosing based on features', fix: 'Choose based on your actual needs' },
];

const GuideTransformationPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-transformation-page">
      <SEO 
        title="Digital Transformation Roadmap - Modernize Your Business"
        description="A strategic guide to modernizing your business operations with technology that fits your needs. From assessment to optimization."
        keywords="digital transformation, business modernization, technology strategy, business automation, digital strategy, technology roadmap, business process improvement"
        url="/resources/guides/digital-transformation"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-cyan-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/resources/guides" className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Guides
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 rounded-full mb-6">
            <Lightbulb className="h-4 w-4 text-cyan-600" />
            <span className="text-sm font-medium text-cyan-700">Strategy</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-carbon-black mb-4" data-testid="guide-title">
            Digital Transformation Roadmap
          </h1>
          <p className="text-xl text-slate-gray leading-relaxed">
            A strategic guide to modernizing your business operations with technology that fits your needs - not the other way around.
          </p>
        </div>
      </section>

      {/* What It Means */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-carbon-black mb-6">What Digital Transformation Really Means</h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-slate-gray leading-relaxed">
              Digital transformation isn't about having the latest gadgets or trendiest software. It's about using technology strategically to solve real business problems, serve customers better, and operate more efficiently.
            </p>
            <p className="text-slate-gray leading-relaxed">
              For small and medium businesses, this usually means: eliminating manual processes that waste time, making information accessible to the people who need it, and creating better experiences for customers. It's evolution, not revolution.
            </p>
          </div>
        </div>
      </section>

      {/* Stages */}
      {stages.map((stage, stageIdx) => {
        const IconComponent = stage.icon;
        return (
          <section key={stageIdx} className={`py-12 sm:py-16 ${stageIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-white">{stage.stage}</span>
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black">{stage.title}</h2>
                  <p className="text-slate-gray mt-1">{stage.description}</p>
                </div>
              </div>

              {stage.questions && (
                <div className="mb-8">
                  <h3 className="font-semibold text-carbon-black mb-4">Questions to Answer:</h3>
                  <div className="space-y-2">
                    {stage.questions.map((q, qIdx) => (
                      <div key={qIdx} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100">
                        <span className="text-cyan-500 font-bold">?</span>
                        <span className="text-slate-gray">{q}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.actions && (
                <div>
                  <h3 className="font-semibold text-carbon-black mb-4">Actions to Take:</h3>
                  <div className="space-y-2">
                    {stage.actions.map((action, aIdx) => (
                      <div key={aIdx} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                        <span className="text-slate-gray">{action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.framework && (
                <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm">
                  <h3 className="font-bold text-carbon-black mb-4">{stage.framework.title}</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {stage.framework.items.map((item, iIdx) => (
                      <div key={iIdx} className="p-4 bg-gray-50 rounded-lg">
                        <div className="font-semibold text-cyan-600 mb-1">{item.priority}</div>
                        <div className="text-sm text-carbon-black mb-1">{item.criteria}</div>
                        <div className="text-xs text-slate-gray">Example: {item.example}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.essentials && (
                <div className="space-y-4">
                  {stage.essentials.map((item, eIdx) => (
                    <div key={eIdx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                      <h4 className="font-bold text-cyan-600 mb-2">{item.area}</h4>
                      <p className="text-carbon-black text-sm mb-1"><strong>What:</strong> {item.what}</p>
                      <p className="text-slate-gray text-sm"><strong>Why:</strong> {item.why}</p>
                    </div>
                  ))}
                </div>
              )}

              {stage.opportunities && (
                <div className="space-y-4">
                  {stage.opportunities.map((opp, oIdx) => (
                    <div key={oIdx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                      <h4 className="font-bold text-carbon-black mb-3">{opp.process}</h4>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="p-3 bg-red-50 rounded-lg">
                          <div className="text-xs font-semibold text-red-600 mb-1">MANUAL</div>
                          <p className="text-sm text-slate-gray">{opp.manual}</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="text-xs font-semibold text-green-600 mb-1">AUTOMATED</div>
                          <p className="text-sm text-slate-gray">{opp.automated}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {stage.improvements && (
                <div className="space-y-3">
                  {stage.improvements.map((imp, impIdx) => (
                    <div key={impIdx} className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                      <div className="w-24 flex-shrink-0">
                        <div className="text-sm font-semibold text-cyan-600">{imp.touchpoint}</div>
                      </div>
                      <div className="text-slate-gray">{imp.enhancement}</div>
                    </div>
                  ))}
                </div>
              )}

              {stage.metrics && (
                <div className="mb-8">
                  <h3 className="font-semibold text-carbon-black mb-4">Key Metrics to Track:</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {stage.metrics.map((m, mIdx) => (
                      <div key={mIdx} className="bg-white rounded-lg p-4 border border-gray-100">
                        <div className="font-semibold text-carbon-black">{m.metric}</div>
                        <div className="text-sm text-slate-gray">{m.measure}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stage.practices && (
                <div>
                  <h3 className="font-semibold text-carbon-black mb-4">Ongoing Practices:</h3>
                  <div className="space-y-2">
                    {stage.practices.map((practice, pIdx) => (
                      <div key={pIdx} className="flex items-center gap-3">
                        <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                        <span className="text-slate-gray">{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* Common Mistakes */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8 text-center">Common Mistakes to Avoid</h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {commonMistakes.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="flex items-start gap-3 mb-2">
                  <Shield className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span className="font-semibold text-carbon-black">{item.mistake}</span>
                </div>
                <div className="flex items-start gap-3 ml-8">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-gray">{item.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Ready to Start Your Transformation?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            We help businesses navigate digital transformation at a pace that makes sense. Let's create a roadmap for your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/resources/consultation">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-cyan-400 text-cyan-600 hover:bg-cyan-50 transition-all w-full sm:w-auto">
                More Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuideTransformationPage;
